import { useTranslation } from 'react-i18next';
import { Plus, FileText, Clock, Sparkles, ArrowRight, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDocumentStore, useAuthStore } from '../lib/store';
import { documentsApi } from '../lib/api';
import { cn, formatDate } from '../lib/utils';

export default function Home() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { documents, addDocument } = useDocumentStore();
  const { user } = useAuthStore();
  const isRtl = i18n.language === 'ar';
  const ArrowIcon = isRtl ? ArrowLeft : ArrowRight;

  const recentDocuments = documents
    .filter((doc) => !doc.isArchived)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 6);

  const favoriteDocuments = documents.filter(
    (doc) => doc.isFavorited && !doc.isArchived
  );

  const handleCreateDocument = async () => {
    try {
      const response = await documentsApi.create({ title: '' });
      addDocument(response.document);
      navigate(`/app/document/${response.document.id}`);
    } catch {
      const newDoc = {
        id: crypto.randomUUID(),
        title: '',
        content: '',
        parentId: null,
        ownerId: user?.id || 'local',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isArchived: false,
        isFavorited: false,
      };
      addDocument(newDoc);
      navigate(`/app/document/${newDoc.id}`);
    }
  };

  return (
    <div className="notion-container animate-fade-in">
      {/* Welcome Section */}
      <div className="mb-12">
        <h1 className="notion-heading-1 mb-2">
          {user?.name ? `${t('home.welcome')}, ${user.name}` : t('app.name')}
        </h1>
        <p className="text-muted-foreground text-lg">{t('app.description')}</p>
      </div>

      {/* Quick Actions */}
      <div className="mb-12">
        <h2 className="notion-heading-3 mb-4 text-muted-foreground">
          {t('home.quickActions')}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* New Document */}
          <button
            onClick={handleCreateDocument}
            className={cn(
              'group flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-8',
              'text-muted-foreground transition-all hover:border-primary hover:text-foreground',
              'hover:bg-secondary/50 hover:shadow-lg'
            )}
          >
            <div className="rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 p-4 text-white shadow-lg transition-transform group-hover:scale-110">
              <Plus className="h-6 w-6" />
            </div>
            <span className="font-medium">{t('document.newPage')}</span>
          </button>

          {/* From Template (placeholder) */}
          <div
            className={cn(
              'flex flex-col items-center justify-center gap-3 rounded-xl border p-8',
              'text-muted-foreground bg-secondary/30'
            )}
          >
            <div className="rounded-xl bg-secondary p-4">
              <Sparkles className="h-6 w-6" />
            </div>
            <span className="font-medium">{t('home.fromTemplate')}</span>
            <span className="text-xs">{t('common.comingSoon')}</span>
          </div>
        </div>
      </div>

      {/* Favorites */}
      {favoriteDocuments.length > 0 && (
        <div className="mb-12">
          <h2 className="notion-heading-3 mb-4 flex items-center gap-2 text-muted-foreground">
            ⭐ {t('sidebar.favorites')}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {favoriteDocuments.map((doc) => (
              <button
                key={doc.id}
                onClick={() => navigate(`/app/document/${doc.id}`)}
                className={cn(
                  'group flex flex-col items-start gap-3 rounded-xl border p-4 text-start',
                  'transition-all hover:border-primary hover:shadow-lg hover:bg-card'
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{doc.icon || '📄'}</span>
                  <span className="font-medium truncate">
                    {doc.title || t('document.untitled')}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {t('document.lastEdited')} {formatDate(doc.updatedAt, i18n.language)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recent Documents */}
      {recentDocuments.length > 0 && (
        <div>
          <h2 className="notion-heading-3 mb-4 flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4" />
            {t('sidebar.recent')}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {recentDocuments.map((doc) => (
              <button
                key={doc.id}
                onClick={() => navigate(`/app/document/${doc.id}`)}
                className={cn(
                  'group flex items-center gap-3 rounded-lg border p-3 text-start',
                  'transition-all hover:border-primary hover:shadow-md'
                )}
              >
                <span className="text-xl shrink-0">{doc.icon || '📄'}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {doc.title || t('document.untitled')}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatDate(doc.updatedAt, i18n.language)}
                  </div>
                </div>
                <ArrowIcon className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {documents.length === 0 && (
        <div className="text-center py-16">
          <div className="text-6xl mb-4">📝</div>
          <h3 className="notion-heading-2 mb-2">{t('home.noDocuments')}</h3>
          <p className="text-muted-foreground mb-6">{t('home.createFirst')}</p>
          <button onClick={handleCreateDocument} className="btn-primary">
            <Plus className="h-4 w-4" />
            {t('document.newPage')}
          </button>
        </div>
      )}
    </div>
  );
}
